<script setup>
import { ref, computed, watch } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import Icon from '@/components/shell/Icon.vue'
import { store } from '@/store/appStore.js'
import { STAGES } from '@/data/mockData.js'
import { REJECTION_REASONS } from '@/store/appStore.js'

const lightFilter = ref('All'); const search = ref(''); const drawerOrderNo = ref(null)
const rejectRemarksInput = ref(''); const rejectReasonInput = ref(REJECTION_REASONS[0]); const lrNumberInput = ref('')
const isPharmacyRole = computed(() => store.state.role === 'pharmacy')
const isTelecaller = computed(() => store.state.role === 'telecaller')
const isWarehouse = computed(() => store.state.role === 'warehouse')
const isAdmin = computed(() => store.state.role === 'admin')
const baseOrders = computed(() => isPharmacyRole.value ? store.data.myOrders.value : store.data.orders.value)
const drawerOrder = computed(() => drawerOrderNo.value ? baseOrders.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const RANK = { Red:0, Orange:1, Yellow:2, Green:3 }

// BUG (P0 regression): extending order history from 30 to 120 orders means
// the single order sitting at "Telecaller Validation" at any given moment is
// now one row among ~100 Green, already-Delivered-or-later rows, sorted to
// the very bottom by the traffic-light rank below. The approve/reject action
// genuinely worked (verified in Phase 4 and again after P0) — it just became
// practically unfindable without using global search. Actionable-for-my-role
// orders now sort to the top, ahead of traffic light, so the one order a
// Telecaller/Warehouse user actually needs to act on is never buried.
function isActionableForRole(o) {
  if (o.rejected) return false
  if (isTelecaller.value || isAdmin.value) { if (o.currentStage === 'Telecaller Validation') return true }
  if (isWarehouse.value || isAdmin.value) { if (o.currentStage === 'Stock Allocation' || o.currentStage === 'Dispatch Pending') return true }
  if (isPharmacyRole.value) { if (o.currentStage === 'Order Received' && !o.otpConfirmed) return true }
  return false
}
const actionableCount = computed(() => baseOrders.value.filter(isActionableForRole).length)

const orders = computed(() => {
  const q = search.value.trim().toLowerCase()
  let list = lightFilter.value==='All' ? baseOrders.value : baseOrders.value.filter(o=>o.trafficLight===lightFilter.value)
  if (q) list = list.filter(o=>o.orderNo.toLowerCase().includes(q)||o.pharmacyName.toLowerCase().includes(q)||o.productName.toLowerCase().includes(q))
  return [...list].sort((a,b) => {
    const actionDiff = (isActionableForRole(b)?1:0) - (isActionableForRole(a)?1:0)
    if (actionDiff !== 0) return actionDiff
    return RANK[a.trafficLight]-RANK[b.trafficLight]
  })
})
const counts = computed(() => ({ Green: baseOrders.value.filter(o=>o.trafficLight==='Green').length, Yellow: baseOrders.value.filter(o=>o.trafficLight==='Yellow').length, Orange: baseOrders.value.filter(o=>o.trafficLight==='Orange').length, Red: baseOrders.value.filter(o=>o.trafficLight==='Red').length }))
const stageIdx = computed(() => drawerOrder.value ? STAGES.indexOf(drawerOrder.value.currentStage) : -1)
const isLastStage = computed(() => stageIdx.value === STAGES.length - 1)
const setFilter = light => { lightFilter.value = lightFilter.value===light ? 'All' : light }
const openDrawer = o => { drawerOrderNo.value = o.orderNo; rejectRemarksInput.value = ''; rejectReasonInput.value = REJECTION_REASONS[0]; lrNumberInput.value = '' }

// Phase 3: land here from a global search result. Uses a watcher (not just
// onMounted) because AppShell only remounts this component when the module
// or scenario changes — searching again while already on Order Tracking
// wouldn't remount it, so onMounted alone would miss the second search.
watch(() => store.state.pendingFocus, f => {
  if (f && f.type === 'order') {
    const match = baseOrders.value.find(o => o.orderNo === f.id)
    if (match) openDrawer(match)
    store.clearPendingFocus()
  }
}, { immediate: true })

const advance = () => { if (drawerOrderNo.value) store.advanceOrderStage(drawerOrderNo.value) }
const reject = () => {
  if (!drawerOrderNo.value || !rejectRemarksInput.value.trim()) return
  store.rejectOrder(drawerOrderNo.value, rejectRemarksInput.value.trim(), rejectReasonInput.value)
  rejectRemarksInput.value = ''
}
const dispatch = () => {
  if (!drawerOrderNo.value || !lrNumberInput.value.trim()) return
  store.markDispatched(drawerOrderNo.value, lrNumberInput.value.trim())
  lrNumberInput.value = ''
}
const confirmOtp = () => { if (drawerOrderNo.value) store.confirmOrderOtp(drawerOrderNo.value) }
const allocate = () => { if (drawerOrderNo.value) store.allocateStock(drawerOrderNo.value) }

// Phase 4: role-specific action workbenches. Design note — the blueprint's
// 8-stage flow inserts "Pharmacy OTP" between Order Received and Telecaller
// Validation. Rather than adding a 9th named stage (which would shift every
// STAGES.indexOf() comparison the already-verified Invoice & Billing module
// relies on), OTP confirmation is a gate on the Order Received stage that
// auto-advances to Telecaller Validation once confirmed — see
// store.confirmOrderOtp. Admin can see and use every action below as a
// superuser convenience for demoing any hop without re-running every gate.
const showOtpConfirm    = computed(() => drawerOrder.value && !drawerOrder.value.rejected && drawerOrder.value.currentStage === 'Order Received' && !drawerOrder.value.otpConfirmed && (isPharmacyRole.value || isAdmin.value))
const showTelecallerDesk= computed(() => drawerOrder.value && !drawerOrder.value.rejected && drawerOrder.value.currentStage === 'Telecaller Validation' && (isTelecaller.value || isAdmin.value))
const showWarehouseAllocate = computed(() => drawerOrder.value && !drawerOrder.value.rejected && drawerOrder.value.currentStage === 'Stock Allocation' && (isWarehouse.value || isAdmin.value))
const showDispatchDesk  = computed(() => drawerOrder.value && !drawerOrder.value.rejected && drawerOrder.value.currentStage === 'Dispatch Pending' && (isWarehouse.value || isAdmin.value))
// Hops the blueprint doesn't name a specific desk for (Warehouse Processing →
// Dispatch Pending, Dispatched → Invoice Generated, Invoice Generated →
// Delivered) fall back to a plain "Advance" action for Warehouse/Admin.
const showGenericAdvance = computed(() => {
  if (!drawerOrder.value || drawerOrder.value.rejected || isLastStage.value) return false
  if (!(isWarehouse.value || isAdmin.value)) return false
  return !showWarehouseAllocate.value && !showDispatchDesk.value && drawerOrder.value.currentStage !== 'Order Received' && drawerOrder.value.currentStage !== 'Telecaller Validation'
})
const canReject = computed(() => drawerOrder.value && !drawerOrder.value.rejected && !isLastStage.value && (isTelecaller.value || isWarehouse.value || isAdmin.value))

// Phase 4: TAT-in-stage badge + fulfillment confidence score — both
// deterministic (hashStr-derived), not random-per-render, so they're stable
// across re-opens rather than flickering to a new value every click.
const tatBadge = computed(() => {
  if (!drawerOrder.value) return ''
  const h = store.hashStr(drawerOrder.value.orderNo + drawerOrder.value.currentStage)
  const hours = h % 9          // 0-8h
  const mins = (h >> 3) % 60   // 0-59m
  return `In ${drawerOrder.value.currentStage} queue: ${hours}h ${mins}m`
})
const confidence = computed(() => {
  if (!drawerOrder.value) return null
  const light = drawerOrder.value.trafficLight
  const h = store.hashStr(drawerOrder.value.orderNo) % 10
  const bands = { Green: [90, h], Yellow: [72, h % 8], Orange: [50, h % 10], Red: [20, h % 15] }
  const [base, jitter] = bands[light] || bands.Green
  const pct = Math.min(99, base + jitter)
  const label = pct >= 85 ? 'High Certainty' : pct >= 60 ? 'Moderate Certainty' : pct >= 40 ? 'At Risk' : 'Critical'
  const dot = pct >= 85 ? '🟢' : pct >= 60 ? '🟡' : pct >= 40 ? '🟠' : '🔴'
  return { pct, label, dot }
})
</script>
<template>
  <div>
    <div class="card module-header">
      <span class="eyebrow">Module 4 & 6 — Real-Time Order Tracking + Traffic Light Status</span>
      <h2>{{ baseOrders.length }} order{{ baseOrders.length===1?'':'s' }}{{ isPharmacyRole ? ' for '+store.activePharmacy.value?.pharmacyName : ' across 8 lifecycle stages' }}</h2>
    </div>
    <div v-if="!baseOrders.length" class="card empty-state">
      <div class="icon-wrap"><Icon name="truck" :size="26" /></div>
      <h3>No orders to show</h3><p class="muted">{{ store.activePharmacy.value?.pharmacyName }} has no orders in this scenario.</p>
    </div>
    <template v-else>
      <div v-if="actionableCount > 0" class="scenario-banner growth" style="margin-bottom:16px;">
        <div><strong>⚡ {{ actionableCount }} order{{ actionableCount===1?'':'s' }} need{{ actionableCount===1?'s':'' }} your action</strong> — sorted to the top of the table below.</div>
      </div>
      <div class="stat-row">
        <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="setFilter('Green')"><div class="stat-value" style="color:var(--status-green);">{{ counts.Green }}</div><div class="stat-label">Green — normal</div></button>
        <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="setFilter('Yellow')"><div class="stat-value" style="color:#97731f;">{{ counts.Yellow }}</div><div class="stat-label">Yellow — minor delay</div></button>
        <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="setFilter('Orange')"><div class="stat-value" style="color:var(--status-orange);">{{ counts.Orange }}</div><div class="stat-label">Orange — action required</div></button>
        <button class="card stat-card" style="text-align:left;cursor:pointer;" @click="setFilter('Red')"><div class="stat-value" style="color:var(--status-red);">{{ counts.Red }}</div><div class="stat-label">Red — SLA breached</div></button>
      </div>
      <div class="card">
        <div class="toolbar">
          <input class="search-input" v-model="search" placeholder="Search order no., pharmacy or product…" />
          <span class="result-count">{{ lightFilter==='All'?'All statuses':'Filtered to '+lightFilter }}</span>
          <button v-if="lightFilter!=='All'" class="filter-select" style="cursor:pointer;" @click="lightFilter='All'">Clear</button>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Product</th><th>Stage</th><th>TAT (days)</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="o in orders" :key="o.orderNo" class="clickable" :style="isActionableForRole(o) ? 'border-left:3px solid var(--color-accent);' : ''" @click="openDrawer(o)">
                <td class="cell-strong">{{ o.orderNo }} <span v-if="isActionableForRole(o)" class="tier-badge Gold" style="margin-left:6px;">Action needed</span></td><td>{{ o.pharmacyName }}</td><td>{{ o.productName }}</td><td class="cell-muted">{{ o.rejected ? 'Rejected' : o.currentStage }}</td><td class="cell-muted">{{ o.tatDays }}</td><td><StatusPill :status="o.trafficLight" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo||''" @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Pharmacy</div>{{ drawerOrder.pharmacyName }}</div>
          <div><div class="label">Product</div>{{ drawerOrder.productName }}</div>
          <div><div class="label">Quantity</div>{{ drawerOrder.quantity }}</div>
          <div><div class="label">Value</div>${{ drawerOrder.orderValue.toLocaleString() }}</div>
          <div><div class="label">Scheme</div>{{ drawerOrder.schemeApplied||'—' }}</div>
          <div><div class="label">TAT</div>{{ drawerOrder.tatDays }} days</div>
        </div>
        <div class="eyebrow" style="margin-bottom:10px;">Order Lifecycle</div>
        <div class="stepper">
          <div v-for="(s,i) in STAGES" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<stageIdx, current: i===stageIdx }"></div>
              <div v-if="i<STAGES.length-1" class="stepper-line" :class="{ done: i<stageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===stageIdx?'current':(i<stageIdx?'':'pending')">{{ s }}</div>
          </div>
        </div>
        <div class="eyebrow" style="margin:18px 0 8px;">Status</div>
        <div style="display:flex; align-items:center; gap:14px; flex-wrap:wrap;">
          <StatusPill :status="drawerOrder.trafficLight" />
          <span v-if="!drawerOrder.rejected && confidence" style="font-size:12.5px; color:var(--color-text-muted);">
            {{ confidence.dot }} {{ confidence.pct }}% {{ confidence.label }}
          </span>
        </div>
        <div v-if="!drawerOrder.rejected && !isLastStage" style="margin-top:8px; font-size:12px; color:var(--color-text-muted); display:flex; align-items:center; gap:5px;">
          <Icon name="calendar" :size="12" /> {{ tatBadge }}
        </div>
        <div v-if="drawerOrder.rejected" class="oos-remarks" style="margin-top:14px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px;color:var(--color-text-muted);">Rejected — {{ drawerOrder.rejectReason }}</strong>
          {{ drawerOrder.rejectRemarks }}
        </div>
        <div v-if="drawerOrder.lrNumber" class="oos-remarks" style="margin-top:14px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px;color:var(--color-text-muted);">LR Tracking No.</strong>
          {{ drawerOrder.lrNumber }}
        </div>

        <div v-if="!drawerOrder.rejected && isLastStage" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <span class="cell-muted" style="font-size:12.5px;">✓ Order has reached the final stage (Delivered).</span>
        </div>

        <!-- Phase 4: role-specific action workbenches. Each block only shows
             for the role that owns that desk in the BRD (plus Admin as a
             superuser), and only at the stage where that action applies. -->
        <div v-if="showOtpConfirm" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Pharmacy — Order Confirmation</div>
          <p class="muted" style="font-size:12.5px;margin:0 0 10px;">Confirm this order via a mock OTP sent to the pharmacy's registered mobile number to release it to the Telecaller queue.</p>
          <button class="btn-primary" @click="confirmOtp">Confirm Order via OTP</button>
        </div>

        <div v-if="showTelecallerDesk" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Telecaller Desk</div>
          <button class="btn-primary" style="margin-right:8px;" @click="advance">Approve &amp; Send to Warehouse</button>
        </div>

        <div v-if="showWarehouseAllocate" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Warehouse Execution Desk</div>
          <button class="btn-primary" @click="allocate">Reserve &amp; Allocate Stock</button>
        </div>

        <div v-if="showDispatchDesk" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Dispatch Desk</div>
          <div class="form-group">
            <label>LR Tracking Number (required to mark Dispatched)</label>
            <div style="display:flex;gap:8px;">
              <input class="form-input" v-model="lrNumberInput" placeholder="e.g. LR-2026-4471" />
              <button class="btn-primary" :disabled="!lrNumberInput.trim()" @click="dispatch">Mark Dispatched</button>
            </div>
          </div>
        </div>

        <div v-if="showGenericAdvance" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Advance Order</div>
          <p class="muted" style="font-size:12px;margin:0 0 10px;">No dedicated desk is defined for this hop — advances the order to the next stage.</p>
          <button class="btn-primary" @click="advance">Advance to next stage</button>
        </div>

        <div v-if="canReject" style="border-top:1px solid var(--color-border);margin-top:18px;padding-top:16px;">
          <div class="form-group">
            <label>Reject Order (reason + remarks required)</label>
            <select class="form-select" v-model="rejectReasonInput" style="margin-bottom:8px;">
              <option v-for="r in REJECTION_REASONS" :key="r">{{ r }}</option>
            </select>
            <div style="display:flex;gap:8px;">
              <input class="form-input" v-model="rejectRemarksInput" placeholder="Reason for rejection…" />
              <button class="filter-select" style="cursor:pointer;color:var(--status-red);" :disabled="!rejectRemarksInput.trim()" @click="reject">Reject</button>
            </div>
          </div>
        </div>
      </template>
    </DetailDrawer>
  </div>
</template>